/**
 * useGerak — mesin gerak tema v3. Pasangan JS dari `assets/css/gerak.css`.
 *
 * Dipanggil SEKALI per halaman (di <script setup>). Ia menangani dua hal yang
 * memang tidak bisa dikerjakan CSS sendirian:
 *   1. memunculkan elemen saat ia masuk layar (IntersectionObserver);
 *   2. memiringkan kartu mengikuti kursor (hanya perangkat berpenunjuk halus).
 *
 * Semua yang lain — ornamen hero, denyut, akordeon, tekan tombol — murni CSS
 * dan tidak menyentuh file ini sama sekali.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DUA KEPUTUSAN YANG MENENTUKAN FILE INI
 * ────────────────────────────────────────────────────────────────────────────
 *
 * (a) YANG DISEMBUNYIKAN HANYA YANG MEMANG DI LUAR LAYAR.
 *     Pola "sembunyikan semua di CSS, lalu JS memunculkan" adalah pola yang
 *     salah untuk kita: kalau bundel JS gagal diunduh — hal biasa di jaringan
 *     seluler yang putus-nyambung — halaman jadi kosong melompong padahal
 *     HTML-nya sudah sampai. Di sini keadaan tersembunyi TIDAK PERNAH ada di
 *     HTML. JS-lah yang memasangnya saat mount, dan hanya pada elemen yang
 *     posisinya sudah di bawah lipatan layar. Konsekuensinya:
 *       · JS gagal / dimatikan  → tidak ada yang tersembunyi, halaman utuh;
 *       · konten paruh atas     → tampil seketika, tanpa kedip, tanpa menunggu;
 *       · hanya yang di bawah   → yang mendapat animasi muncul.
 *
 * (b) DI HP, NOL LISTENER TILT.
 *     Tilt dijaga oleh `(hover: hover) and (pointer: fine)`. Di HP Android
 *     tidak ada satu pun `pointermove` yang terpasang, jadi tidak ada biaya
 *     yang perlu ditawar. Kedalaman di HP datang dari CSS (`.ca-lapis-3d`).
 */

/** Sudut maksimum tilt. Harus sama dengan `--gerak-tilt` di gerak.css. */
const TILT_MAKS = 5

/** Elemen dianggap "di luar layar" bila puncaknya di bawah 90% tinggi layar. */
const AMBANG_LIPATAN = 0.9

export function useGerak() {
  // Daftar pembersih dibuat di tingkat setup, dan onBeforeUnmount didaftarkan
  // di tingkat setup pula. Mendaftarkannya dari DALAM onMounted tidak sah:
  // di sana tidak ada effect scope yang aktif, sehingga pembersihnya tidak
  // pernah terpanggil dan listener-nya bocor tiap kali halaman ditinggalkan.
  const bersihkan: Array<() => void> = []

  onMounted(() => {
    // Preferensi pengguna dicek DULUAN, sebelum apa pun dipasang. Kalau ia
    // minta gerak dikurangi: tidak ada observer, tidak ada listener, tidak ada
    // kelas yang ditulis. Halaman tampil apa adanya — yang memang tujuannya.
    // (gerak.css tetap memasang jaring pengaman !important seandainya
    // preferensi ini dinyalakan setelah halaman terbuka.)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    pasangMuncul(bersihkan)
    pasangTilt(bersihkan)
    pasangParallax(bersihkan)
  })

  onBeforeUnmount(() => {
    for (const b of bersihkan) b()
    bersihkan.length = 0
  })
}

/* ==========================================================================
   1. MUNCUL SAAT DIGULIR
   ========================================================================== */
function pasangMuncul(bersihkan: Array<() => void>) {
  // Peramban tanpa IntersectionObserver (WebView Android sangat lama) tidak
  // mendapat animasi sama sekali — dan itu benar. Yang HARAM adalah memberi
  // mereka halaman yang isinya tersembunyi selamanya.
  if (!('IntersectionObserver' in window)) return

  const sasaran = Array.from(document.querySelectorAll<HTMLElement>('[data-muncul]'))
  if (!sasaran.length) return

  // BACA SEMUA DULU, BARU TULIS. Membaca rect lalu menulis kelas bergantian
  // memaksa layout ulang tiap iterasi (layout thrashing). Dipisah begini,
  // seluruh pengukuran hanya menimbulkan SATU layout paksa, sekali seumur
  // halaman — di HP kelas bawah bedanya nyata.
  const ambang = window.innerHeight * AMBANG_LIPATAN
  const jarakAtas = sasaran.map((el) => el.getBoundingClientRect().top)

  const diLuarLayar = sasaran.filter((_, i) => jarakAtas[i]! > ambang)
  if (!diLuarLayar.length) return

  for (const el of diLuarLayar) el.classList.add('ca-sembunyi')

  /** Memunculkan satu elemen dan melepasnya dari pengamatan. */
  const munculkan = (el: HTMLElement) => {
    // Dua kelas ditukar dalam satu perubahan gaya. Transisinya tetap jalan
    // karena peramban membaca `transition-property` dari gaya SESUDAH
    // perubahan, dan `.ca-muncul` memang mendeklarasikannya. (`.ca-sembunyi`
    // sengaja TIDAK punya transisi — lihat gerak.css bagian 2.)
    el.classList.remove('ca-sembunyi')
    el.classList.add('ca-muncul')
  }

  // ── JARING PENGAMAN ──────────────────────────────────────────────────────
  // Menyembunyikan konten lalu menyerahkan nasibnya pada IntersectionObserver
  // berarti: kalau observer-nya tidak pernah mengirim callback, teks itu
  // hilang SELAMANYA. Itu kegagalan terburuk yang bisa dialami halaman ini —
  // orang sedang mengejar tenggat, dan halamannya kosong.
  //
  // Peramban yang sehat SELALU mengirim callback pertama sesaat setelah
  // observe(), meski isinya "belum terlihat". Jadi kalau setelah 3 detik
  // belum ada satu pun callback yang datang, observer-nya memang tidak
  // berfungsi — dan kita buka semuanya. Konsekuensinya cuma kehilangan
  // animasi; konsekuensi tanpa jaring ini adalah kehilangan kontennya.
  //
  // (Bukan kekhawatiran hipotetis: saat pekerjaan ini diperiksa, ditemukan
  // lingkungan peramban yang IntersectionObserver-nya tidak pernah mengirim
  // callback sama sekali padahal elemennya jelas berada di dalam viewport.)
  let pernahDipanggil = false

  const pengamat = new IntersectionObserver(
    (entri) => {
      pernahDipanggil = true
      for (const e of entri) {
        if (!e.isIntersecting) continue
        const el = e.target as HTMLElement

        munculkan(el)

        // Sekali muncul, selesai. Elemen dilepas dari pengamatan supaya tidak
        // pernah dianimasikan ulang saat pengguna menggulir naik-turun.
        pengamat.unobserve(el)
      }
    },
    {
      // Dipicu sedikit SEBELUM elemen benar-benar terlihat, supaya saat mata
      // sampai ke sana animasinya sudah selesai — bukan baru mulai.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    }
  )

  for (const el of diLuarLayar) pengamat.observe(el)

  const jaring = window.setTimeout(() => {
    if (pernahDipanggil) return
    for (const el of diLuarLayar) munculkan(el)
    pengamat.disconnect()
  }, 3000)

  bersihkan.push(() => {
    clearTimeout(jaring)
    pengamat.disconnect()
  })
}

/* ==========================================================================
   2. TILT KARTU MENGIKUTI KURSOR
   ========================================================================== */
function pasangTilt(bersihkan: Array<() => void>) {
  // Gerbang perangkat. `pointer: fine` menyaring layar sentuh; `hover: hover`
  // menyaring perangkat yang tidak punya keadaan "melayang di atas" sama
  // sekali. Kalau tidak lolos, fungsi ini keluar tanpa memasang apa pun.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

  const grup = Array.from(document.querySelectorAll<HTMLElement>('[data-tilt-grup]'))
  if (!grup.length) return

  for (const g of grup) {
    let kartuAktif: HTMLElement | null = null
    let x = 0
    let y = 0
    let rafId = 0

    const tulis = () => {
      rafId = 0
      const kartu = kartuAktif
      if (!kartu) return

      // Rect diukur di dalam rAF, sekali per frame, dan hanya selama kursor
      // benar-benar berada di atas sebuah kartu. Ini sengaja tidak di-cache:
      // cache akan basi begitu halaman digulir sambil hover, dan menambal itu
      // butuh listener scroll — persis yang tidak kita inginkan. Satu
      // pengukuran per frame di desktop harganya nol.
      const r = kartu.getBoundingClientRect()
      if (!r.width || !r.height) return

      const nx = (x - r.left) / r.width - 0.5   // -0.5 … 0.5
      const ny = (y - r.top) / r.height - 0.5

      // rotateY mengikuti sumbu X kursor; rotateX dibalik tandanya supaya
      // kartu terasa "ditekan menjauh" di sisi yang ditunjuk — kalau tidak
      // dibalik, arahnya terasa terbalik dan justru bikin pusing.
      kartu.style.setProperty('--ca-tilt-y', `${(nx * 2 * TILT_MAKS).toFixed(2)}deg`)
      kartu.style.setProperty('--ca-tilt-x', `${(-ny * 2 * TILT_MAKS).toFixed(2)}deg`)
    }

    const datarkan = (kartu: HTMLElement) => {
      // Atribut dilepas lebih dulu supaya transisi hidup lagi dan kartunya
      // PULANG dengan halus, bukan patah ke posisi datar.
      kartu.removeAttribute('data-tilt-aktif')
      kartu.style.setProperty('--ca-tilt-x', '0deg')
      kartu.style.setProperty('--ca-tilt-y', '0deg')
    }

    const bergerak = (e: PointerEvent) => {
      const kartu = (e.target as Element | null)?.closest<HTMLElement>('[data-tilt]') ?? null

      if (kartu !== kartuAktif) {
        if (kartuAktif) datarkan(kartuAktif)
        kartuAktif = kartu
        // Transisi dimatikan hanya SELAMA kursor menempel, supaya tilt-nya
        // mengikuti kursor tanpa jeda.
        if (kartu) kartu.setAttribute('data-tilt-aktif', '')
      }

      if (!kartu) return
      x = e.clientX
      y = e.clientY
      // Satu penulisan gaya per frame, apa pun kerapatan event pointer-nya.
      if (!rafId) rafId = requestAnimationFrame(tulis)
    }

    const keluar = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
        rafId = 0
      }
      if (kartuAktif) {
        datarkan(kartuAktif)
        kartuAktif = null
      }
    }

    g.addEventListener('pointermove', bergerak, { passive: true })
    g.addEventListener('pointerleave', keluar, { passive: true })

    bersihkan.push(() => {
      g.removeEventListener('pointermove', bergerak)
      g.removeEventListener('pointerleave', keluar)
      if (rafId) cancelAnimationFrame(rafId)
    })
  }
}

/* ==========================================================================
   3. PARALLAX HALUS SAAT DIGULIR
   --------------------------------------------------------------------------
   Menggeser elemen DEKORATIF (aura, blob gradien) sedikit berbeda kecepatan
   dari halaman → kesan kedalaman berlapis. Dua keputusan menjaganya murah:

   (a) TANPA listener scroll global. Posisi baca dari `getBoundingClientRect`
       di dalam rAF, dan rAF hanya BERPUTAR selama ada elemen parallax yang
       benar-benar di dalam layar (digerbangi IntersectionObserver). Begitu
       semuanya keluar layar, loop-nya berhenti total — nol biaya saat idle.

   (b) HANYA perangkat berpenunjuk halus. Di HP Android murah — mayoritas
       pengguna kita — fungsi ini keluar sebelum memasang apa pun. Kedalaman di
       HP sudah datang dari CSS statis (ubin ikon .ca-lapis-3d, bayangan
       berlapis) tanpa satu frame kerja pun.

   Hanya `transform` (sumbu Y) yang ditulis, lewat custom property `--par-y`
   yang dibaca aturan `.ca-v3 [data-parallax]` di gerak.css. Nilai bawaannya
   0px, jadi bila fungsi ini tidak jalan sekalipun, elemennya diam di posisi
   rancangannya — tidak ada yang salah tempat.
   ========================================================================== */
function pasangParallax(bersihkan: Array<() => void>) {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
  if (!('IntersectionObserver' in window)) return

  const sasaran = Array.from(document.querySelectorAll<HTMLElement>('[data-parallax]'))
  if (!sasaran.length) return

  const aktif = new Set<HTMLElement>()
  let rafId = 0

  const tulis = () => {
    rafId = 0
    const tengahLayar = window.innerHeight / 2
    for (const el of aktif) {
      const laju = parseFloat(el.dataset.parallax || '0')
      if (!laju) continue
      const r = el.getBoundingClientRect()
      // Jarak pusat elemen dari pusat layar. Elemen di atas pusat → negatif.
      const selisih = r.top + r.height / 2 - tengahLayar
      el.style.setProperty('--par-y', `${(-selisih * laju).toFixed(1)}px`)
    }
    // Loop hanya berlanjut selama masih ada elemen yang terlihat.
    if (aktif.size) rafId = requestAnimationFrame(tulis)
  }

  const pengamat = new IntersectionObserver((entri) => {
    for (const e of entri) {
      const el = e.target as HTMLElement
      if (e.isIntersecting) aktif.add(el)
      else aktif.delete(el)
    }
    // Nyalakan loop hanya bila ada yang masuk layar dan belum berputar.
    if (aktif.size && !rafId) rafId = requestAnimationFrame(tulis)
  })

  for (const el of sasaran) pengamat.observe(el)

  bersihkan.push(() => {
    pengamat.disconnect()
    if (rafId) cancelAnimationFrame(rafId)
    aktif.clear()
  })
}
